import React, { useState, useEffect } from 'react';
import { Dialog, Typography, Button, Box, IconButton } from '@mui/material';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import Loading from 'assets/images/loading.png';
import dayjs from 'dayjs';
import DownloadIcon from '@mui/icons-material/Download';
import CommonPdfViewer from 'component/Common/CommonPdfViewer';

const ViewCommenceEsignPdf = ({
  open,
  onClose,
  taskPath,
  streetName,
  matchessm,
  wStatus,
  statusLastUpdatedAt,
  onInitiateEsign,
  primaryUserTypeValue,
  isArchived = false,
}) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [signedTime, setSignedTime] = useState('');
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    if (taskPath) {
      setPdfLoading(true);
      setTimeout(() => setPdfLoading(false), 2000);
    } else {
      setPdfLoading(false);
    }
  }, [taskPath]);

  useEffect(() => {
    if (statusLastUpdatedAt) {
      const formatted = dayjs(statusLastUpdatedAt).format(
        'HH:mm:ss DD-MM-YYYY'
      );
      setSignedTime(formatted);
    }
  }, [statusLastUpdatedAt]);

  const handleDownload = async () => {
    const res = await fetch(taskPath);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 's66w certificate.pdf';
    a.click();
    URL.revokeObjectURL(url);
  };

  const showInitiateButton = !(
    primaryUserTypeValue === 'Solicitor' ||
    primaryUserTypeValue === 'Buyer' ||
    primaryUserTypeValue === 'Vendor' ||
    wStatus
  );

  const showDownloadButton = primaryUserTypeValue === 'Solicitor';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={matchessm}
      PaperProps={{
        sx: {
          borderRadius: matchessm ? 0 : '5px',
          minWidth: matchessm ? '100%' : '800px',
          maxWidth: 'unset',
          height: matchessm ? '100%' : '100vh',
          margin: '20px auto',
          padding: matchessm ? '20px' : '30px',
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 400,
            fontSize: matchessm ? '10px' : '14px',
            color: '#666666',
          }}
        >
          {streetName}
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding="10px 0"
          flexDirection={matchessm ? 'column' : 'row'}
          gap={matchessm ? 1 : 0}
        >
          <Box display="flex" alignItems="center">
            <Pendingwarning />

            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: matchessm ? '16px' : '24px',
                paddingLeft: '12px',
              }}
            >
              {wStatus ? 'Signed s66W document' : 'E-Signed Contract of Sale'}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                color: '#666666',
              }}
            >
              {signedTime}
            </Typography>

            {showDownloadButton && (
              <IconButton
                onClick={handleDownload}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  color: 'rgb(111,168,224)',
                  border: '1px solid rgb(111, 168, 224)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgb(111,168,224)',
                  },
                  '&:active': {
                    transform: 'scale(0.96)',
                  },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 400,
            fontSize: matchessm ? '12px' : '14px',
            color: '#666666',
            lineHeight: '22px',
            textAlign: 'left',
            mt: 1,
          }}
        >
          View the electronically signed {wStatus ? 's66W' : 'contract of sale'}{' '}
          for reference.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          mt: 2,
          border: '1px solid #ddd',
          backgroundColor: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {pdfLoading ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              alt="Loading"
              src={Loading}
              className="loaderImage"
              style={{ height: '75px', width: '75px' }}
            />
          </Box>
        ) : taskPath ? (
          <div
            className="view-commence-esign-pdf"
            style={{
              height: '100%',
              width: '100%',
            }}
          >
            <CommonPdfViewer
              fileUrl={taskPath}
              defaultScale={1.2}
              onDocumentLoad={(e) => {
                setNumPages(e.doc.numPages);
              }}
              onDocumentLoadError={(error) => {
                console.error('PDF load error:', error);
              }}
            />
          </div>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontFamily: 'Poppins', color: '#666666' }}>
              No PDF file available
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent={showInitiateButton ? 'space-between' : 'flex-end'}
        marginTop={2}
        gap={2}
        flexDirection={matchessm ? 'column-reverse' : 'row'}
      >
        {showInitiateButton && (
          <Button
            variant="contained"
            disabled={primaryUserTypeValue === 'Agent' && isArchived}
            onClick={() => {
              onClose();
              onInitiateEsign();
            }}
            sx={{
              padding: matchessm ? '7px 17px' : '10px 39px',
              height: matchessm ? '35px' : '41px',
              fontSize: matchessm ? '12px' : '14px',
              color: '#FFFFFF',
              fontFamily: 'Poppins',
              background: '#4CA4D6',
              borderRadius: '5px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#4CA4D6',
              },
              '&:disabled': {
                background: '#BFDDF0',
                color: '#FFFFFF',
              },
            }}
          >
            Initiate New Sign Request
          </Button>
        )}

        <Button
          variant="outlined"
          sx={{
            padding: matchessm ? '6px 18px' : '10px 39px',
            height: matchessm ? '35px' : '41px',
            fontSize: matchessm ? '12px' : '14px',
            color: '#666666',
            fontFamily: 'Poppins',
            border: '1px solid rgba(17,35,71,0.1)',
            borderRadius: '5px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              border: '1px solid rgba(17,35,71,0.1)',
            },
          }}
          onClick={onClose}
        >
          Close
        </Button>
      </Box>

      <style>
        {`
          .view-commence-esign-pdf {
            height: 100%;
            width: 100%;
            overflow-y: auto;
            overflow-x: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
            position: relative;
          }

          .view-commence-esign-pdf::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }

          .view-commence-esign-pdf .react-pdf__Document {
            width: 100%;
          }

          .view-commence-esign-pdf .react-pdf__Page {
            display: flex;
            justify-content: center;
            margin: 0 auto;
          }

          .view-commence-esign-pdf .react-pdf__Page__textContent {
            display: none;
          }

          .view-commence-esign-pdf .react-pdf__Page__annotations {
            display: none;
          }

          .view-commence-esign-pdf .react-pdf__Page canvas {
            display: block;
            max-width: 100%;
          }
        `}
      </style>
    </Dialog>
  );
};

export default ViewCommenceEsignPdf;
